export type DashboardVisitTone = 'neutral' | 'primary' | 'info' | 'success' | 'warning' | 'danger';

export type DashboardAppointment = {
  status: string;
  startsAt: string;
  endsAt: string;
  checkedInAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
};

const FLOW_LABELS: Record<string, string> = {
  SCHEDULED: 'Not arrived',
  CONFIRMED: 'Not arrived',
  CHECKED_IN: 'Ready for provider',
  IN_PROGRESS: 'Visit in progress',
  COMPLETED: 'Completed',
  NO_SHOW: 'No-show',
  CANCELED: 'Cancelled',
};

export function dashboardVisitFlowLabel(status: string): string {
  return FLOW_LABELS[status] || status.replaceAll('_', ' ').toLowerCase();
}

export function dashboardVisitFlowTone(status: string): DashboardVisitTone {
  if (status === 'COMPLETED') return 'success';
  if (status === 'CHECKED_IN') return 'warning';
  if (status === 'IN_PROGRESS') return 'primary';
  if (status === 'NO_SHOW' || status === 'CANCELED') return 'danger';
  return 'neutral';
}

export function appointmentDurationMinutes(appointment: DashboardAppointment): number {
  const start = Date.parse(appointment.startsAt);
  const end = Date.parse(appointment.endsAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.max(1, Math.round((end - start) / 60000));
}

export function appointmentFlowProgress(appointment: DashboardAppointment, nowMs: number) {
  const inactive = {
    hasTiming: false,
    timingMode: 'none' as const,
    percent: 0,
    waitingMinutes: 0,
    remainingMinutes: 0,
    overtimeMinutes: 0,
    isOvertime: false,
    detailLabel: '',
    ariaLabel: dashboardVisitFlowLabel(appointment.status),
  };

  if (appointment.status === 'CHECKED_IN') {
    const checkedInMs = Date.parse(appointment.checkedInAt || '');
    if (!Number.isFinite(checkedInMs)) return inactive;
    const waitingMinutes = Math.max(0, Math.floor((nowMs - checkedInMs) / 60000));
    const detailLabel = waitingMinutes > 0 ? `Waiting ${waitingMinutes} min` : 'Waiting now';
    return {
      ...inactive,
      hasTiming: true,
      timingMode: 'waiting' as const,
      waitingMinutes,
      detailLabel,
      ariaLabel: waitingMinutes > 0
        ? `Ready for provider, waiting ${waitingMinutes} minutes`
        : 'Ready for provider, waiting less than one minute',
    };
  }

  if (appointment.status !== 'IN_PROGRESS') return inactive;

  const scheduledStart = Date.parse(appointment.startsAt);
  const scheduledEnd = Date.parse(appointment.endsAt);
  const startedAt = Date.parse(appointment.startedAt || '');
  if (!Number.isFinite(scheduledStart) || !Number.isFinite(scheduledEnd) || scheduledEnd <= scheduledStart || !Number.isFinite(startedAt)) {
    return inactive;
  }

  const durationMs = scheduledEnd - scheduledStart;
  const durationMinutes = Math.max(1, Math.round(durationMs / 60000));
  const flowEnd = startedAt + durationMs;
  const elapsedMs = Math.max(0, nowMs - startedAt);
  const percent = Math.max(0, Math.min(100, Math.round((elapsedMs / durationMs) * 100)));
  const rawRemainingMinutes = nowMs < flowEnd ? Math.max(0, Math.ceil((flowEnd - nowMs) / 60000)) : 0;
  const remainingMinutes = Math.min(durationMinutes, rawRemainingMinutes);
  const overtimeMinutes = nowMs > flowEnd ? Math.max(1, Math.ceil((nowMs - flowEnd) / 60000)) : 0;
  const isOvertime = overtimeMinutes > 0;
  const detailLabel = isOvertime ? `${overtimeMinutes} min over` : remainingMinutes > 0 ? `${remainingMinutes} min left` : 'Duration reached';
  const ariaLabel = isOvertime
    ? `Visit in progress, ${overtimeMinutes} minutes over scheduled duration`
    : remainingMinutes > 0
      ? `Visit in progress, ${remainingMinutes} minutes remaining`
      : 'Visit in progress, scheduled duration reached';

  return {
    hasTiming: true,
    timingMode: 'visit' as const,
    percent,
    waitingMinutes: 0,
    remainingMinutes,
    overtimeMinutes,
    isOvertime,
    detailLabel,
    ariaLabel,
  };
}
